'use client'

import { useTranslate } from '@repo/shared/hooks'
import { FormSchema, useAppForm } from '@repo/ui/hooks'

import { useDepositOnlinePaymentSchema } from '@repo/shared/schemas'
import { useGetBanks, UserUserBankAccount } from '@repo/services'
import { convertToCardNumber } from '@repo/tools'
import {
  Alert,
  Card,
  Col,
  Form,
  FormField,
  FormItemWrapper,
  Icon,
  Row,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Typography,
} from '@repo/ui/components'
import { bankDetailsList } from '@repo/ui/components/bank-card-field/constants'
import { useIsDesktop } from '@repo/ui/hooks'
import { DepositCardToCardCTA } from './deposit-card-to-card.cta'
import { useEffect, useMemo } from 'react'
import { DepositOnlinePaymentAddCard } from '../online-payment/deposit-online-payment.add-card'
import { CardToCardCardItem } from './card-to-card.card-item'

type Card = {
  enName: string | undefined
  card: string | undefined
  sheba: string | undefined
  default: boolean
  id: number
}

export const DepositCardToCardContainer = () => {
  const { t } = useTranslate()
  const { data, isLoading } = useGetBanks({})

  const cards: Card[] = useMemo(
    () =>
      (data?.data?.bankAccounts || [])
        .filter((bank) => bank.type === 'card')
        .map((bank: UserUserBankAccount) => ({
          id: bank.id ?? 0,
          enName: bank.details?.bankName ?? 'Unknown',
          card:
            bank.type === 'card'
              ? convertToCardNumber(Number(bank.data))
              : undefined,
          sheba: bank.details?.iban,
          default: bank.default ?? false,
        })),
    [data?.data?.bankAccounts],
  )
  const defaultCard: Card | undefined = useMemo(
    () => cards.find((c) => c.default),
    [cards],
  )

  const { schema } = useDepositOnlinePaymentSchema()
  const { form } = useAppForm({
    formSchema: schema,
    defaultValues: {
      card: defaultCard ? (defaultCard.card ?? '') : undefined,
    },
  })
  useEffect(() => {
    form.setValue('card', defaultCard ? (defaultCard.card ?? '') : undefined)
  }, [defaultCard, form])

  const onSubmit = (data: FormSchema<typeof schema>) => {}

  const isDesktop = useIsDesktop()

  const getBankIcon = (enName: string) => {
    const logo = bankDetailsList.find(
      (bank) =>
        bank.enNames.some(
          (name) => name.toLowerCase() === enName.toLowerCase(),
        ) || bank.name.toLowerCase() === enName.toLowerCase(),
    )
    return logo?.icon ?? <Icon name='CardLine' size='sm' />
  }

  const productCards = [
    {
      id: 1,
      enName: 'Saman',
      card: '6219861033000005',
    },
    {
      id: 2,
      enName: 'Shahr',
      card: '6219861033000005',
    },

    {
      id: 3,
      enName: 'Postbank',
      card: '6219861033000005',
    },
  ]

  return (
    <Card className='max-w-container-md mx-auto p-xl' withoutBorder>
      {isDesktop && <DepositCardToCardCTA origin='container' />}
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          id='deposit-card-to-card-form'
        >
          <Col className='gap-3xl'>
            <Alert
              color='alert'
              size='md'
              icon={<Icon name='AlertTriangleLine' size='sm' />}
              caption={t('depositCardToCardPage_hint')}
            />

            <FormField
              control={form.control}
              name='card'
              extra={<DepositOnlinePaymentAddCard />}
              render={({ field }) => (
                <Select {...field} onValueChange={field.onChange}>
                  <FormItemWrapper
                    title={t('depositCardToCardPage_select_card')}
                    inputSize='md'
                  >
                    <SelectTrigger inputSize='md' className='w-full'>
                      <SelectValue
                        placeholder={t(
                          'depositCardToCardPage_select_card_placeholder',
                        )}
                      />
                    </SelectTrigger>
                  </FormItemWrapper>
                  <SelectContent className='min-h-10'>
                    {cards.map((c, idx) => (
                      <SelectItem
                        key={idx}
                        value={c.card || c.sheba || String(idx)}
                      >
                        <Row className='gap-xs items-center'>
                          {c.enName && getBankIcon(c.enName)}
                          <Typography size='md'>{c.card || c.sheba}</Typography>
                        </Row>
                      </SelectItem>
                    ))}
                    {cards.length === 0 && !isLoading && (
                      <Col className='min-h-20 text-center m-auto gap-md'>
                        <Typography
                          size='md'
                          className='m-auto'
                          color='text-neutral-surface-80'
                        >
                          {t('depositCardToCardPage_select_card_empty')}
                        </Typography>
                      </Col>
                    )}
                  </SelectContent>
                </Select>
              )}
            />
            <Alert
              color='neutral'
              size='md'
              className='text-center'
              hideIcon
              caption={t('depositCardToCardPage_estimated_time')}
            />
            <Col className='gap-3xs'>
              <Typography size='sm' weight='bold'>
                {t('depositCardToCardPage_allowed_cards')}
              </Typography>
              <Typography size='md'>
                {t('depositCardToCardPage_allowed_cards_hint')}
              </Typography>
            </Col>
            <Col className='gap-xs'>
              {productCards.map((card) => (
                <CardToCardCardItem
                  key={card.id}
                  card={card.card}
                  enName={card.enName ?? ''}
                />
              ))}
            </Col>
          </Col>
        </form>
      </Form>
    </Card>
  )
}
